/**
 * The Mountain, the cave lip and the desert beyond — issue #140, `docs/27` §13.
 *
 * The Great Tree built the west half of #140's surface and `docs/27` §7 wrote
 * down eight rules for the landmarks that were still to come. This is the east
 * half, built against those rules, and it is the other half of the sentence the
 * owner wrote:
 *
 * > To the right of your starting point is a gigantic mountain with an opening
 * > to an enormous cave. There are biomes like a desert and other things on the
 * > other side of the mountain, but like the top of the tree, you cannot yet
 * > reach these. The lip of the cave is too far to reach so you have no choice
 * > but to go in, and as you go down into the cave — this is the real game.
 *
 * ==== THE FUNNEL, AND WHY NOTHING HERE OPENS UP ==========================
 *
 * Both landmarks are **refusals**. The branch stops the player going up; the
 * mountain stops them going across; the cave's east lip is too high to climb to
 * from anywhere on the approach. What is left is a hole in the ground, which is
 * the whole design: `docs/27` §2, *"the only thing you can do is go down. That
 * is the design; it must not be 'fixed' into a hub."*
 *
 * Three gates, in the vocabulary `tests/traversalAgent.ts` owns:
 *
 *     rise <= reach(room),   reach(room) = min(FREE_APEX 56, room - CEILING_COST 2)
 *
 * and **all three of them are `rise` gates**, which is the first thing that is
 * different about this landmark. The branch denies on `room` — it is a ceiling,
 * and the control that proves it is a gate rather than a wall is to carve the
 * ceiling away. Out here every refusal is open to the sky, so `room` is
 * unbounded, `reach` saturates at `FREE_APEX`, and what denies is height. That
 * inverts the control: **you cannot carve away an absence.** The control for a
 * rise gate is to *add* the missing rung — put one one-way rung halfway up and
 * the denied place is reachable in two hops — and `tests/mountain.test.ts` runs
 * exactly that for the lip and for the flank.
 *
 * ==== THE BOUND, WHICH IS NOT THE TREE'S ================================
 *
 * `docs/27` §6.2's finding is that a soft gate has a maximum thickness: the
 * bough denies by its bulk, the control needs `rise = room + T + 5 <= FREE_APEX`,
 * and so `T <= 29`. That number is about a *ceiling* and it does not transfer.
 * A flank is a different geometry and the bound comes out of the other rule —
 * §7 item 6, *everything the gate withholds must be visible from where it is
 * withheld, inside one camera*. The camera is 480 x 270 and the body is centred
 * in it, so a standing player sees **135 cells up** and **240 cells across**.
 *
 *     denial       rise > FREE_APEX 56          or the gate does not refuse
 *     legibility   rise <= SCREEN_H / 2 = 135   or the player is merely stuck
 *
 * A refusal you cannot see over is indistinguishable from a dead end, and
 * `docs/27` §2's whole claim is that geometry saying no reads differently from
 * a locked door saying no. It only does if you can see what it is saying no to.
 * So every riser on this landmark lives in the band `56 < step <= 135`, and the
 * chosen 88 sits 32 above the floor and 47 under the ceiling.
 *
 * **This is why the mountain is a staircase and not one sheer face.** A single
 * 544-cell wall denies just as well and teaches nothing — you cannot see its
 * top, so you cannot tell you were refused. Six risers of 88 refuse the player
 * six times and show them the next tread every time. `docs/27` §7 item 5 said
 * *bulk goes where the player will not stand*; the mountain is 1 076 x 544
 * cells and the gate is one 88-cell step, exactly as the tree is 561 x 751 and
 * the gate is one 20-cell bough.
 *
 * ==== HOW IT IS BUILT ===================================================
 *
 * `greatTree.ts`'s idiom, and §7 item 1: **a module of pure spatial queries
 * plus two generator passes**, one oracle read by both, kinds before
 * `materialise` so the shape reaches the block counters and the connectivity
 * verifier, materials after it so the hot loop gains no branch.
 *
 * One thing is genuinely different and it is worth writing down. The tree
 * *stands on* the ground, so §7 item 2 gives it a `TERRACE` that damps the
 * seeded heightfield to nothing under its feet. The mountain **is** the ground:
 * `mountainGround` replaces `WorldGen.surfaceHeight`'s answer outright inside
 * the landmark's span rather than damping it, which is a stronger form of the
 * same rule and needs no easing at all — a landmark that is the heightfield
 * cannot disagree with the heightfield. The mass under the profile then comes
 * free from `surfaceCarve`, which is why `mountainAt` only has to answer for
 * cells *above* the ground line (§7 item 3) and for the one piece of geometry a
 * heightfield cannot express: the lip's overhang.
 *
 * **Nothing in this file reads the seed**, so `RunTerrain`'s guard is untouched
 * and the mountain is the same mountain in every world — the property #140's
 * persistent overworld rests on (`docs/27` §3).
 *
 * ==== MATERIALS: FOUR, AND THEY SAY WHICH PLACE YOU ARE LOOKING AT ======
 *
 * `docs/27` §8.1 records a collision it declined to fix: the Great Tree is
 * drawn in `WOOD` and `PETRIFIED_WOOD`, and so is the band-0 ground it stands
 * on, so the landmark's silhouette carries the whole load and its palette
 * contributes nothing. **The mountain does not repeat that**, because it does
 * not have to: it is not made of wood.
 *
 *   - `GRANITE` / `LIMESTONE` — the mountain's bulk and its rind. The same
 *     value-step-not-hue-swap the trunk uses, and neither is in the surface
 *     palette.
 *   - `SANDSTONE` / `SAND` — the desert on the far side, and the reason it
 *     reads as *another place* rather than as more mountain from a distance.
 *     `SAND` is loose, so every placed cell carries `Flag.STATIC` for the
 *     reason `prologue.ts` gives about its corridor; a desert that has slumped
 *     into a heap by the time anybody can reach it is not a promise.
 */

import { M, VOID } from '../sim/materials';
import { MAT } from '../sim/materialData';

/** `mountainAt`'s "nothing here". Not a material id — `VOID` is 0 and carves. */
export const MOUNTAIN_NONE = -1;

/**
 * The mountain, as numbers. Restated in `docs/20` §2.1's units in `docs/27` §13.
 *
 * The landmark is 1 076 x 544 cells — 2.24 x 2.01 screens, 107 x 25 bodies —
 * against the Great Tree's 561 x 751 and Terraria's Marble Cave at
 * 0.65-1.23 x 0.58-0.85. There is one of it in the world.
 */
export const MOUNTAIN = {
  /**
   * Ground line at the mountain's foot. Equal to `SURFACE_Y` by construction,
   * and `tests/mountain.test.ts` asserts the equality rather than trusting it —
   * this module may not import `worldgen.ts`, because `worldgen.ts` imports it.
   */
  ground: -40,
  /**
   * The cave's east lip: the first column of the mountain.
   *
   * `DESCENT_X1 + 1`, so the mountain's foot **is** the far wall of the descent
   * mouth and the mouth is literally an opening at the bottom of the mountain.
   * That is the owner's sentence read plainly, and it is what `docs/27` §2's
   * table already said when it recorded the cave mouth as *shipped as
   * `descentShaft`*. Asserted against `DESCENT_X1` in the test.
   */
  caveLipX: 95,
  /**
   * How far the cave's east lip stands above the surface — **gate 1**.
   *
   * The whole point of the sentence *"the lip of the cave is too far to reach
   * so you have no choice but to go in"*. The most generous place a body can
   * launch from is the top of the descent's own west rim, which `descentShaft`
   * stands `RIM_PROUD` cells proud of the ground; from there the rise to the
   * lip is `lipRise - RIM_PROUD`, under open sky, so `reach` is `FREE_APEX`.
   *
   *     denial       lipRise - 16 >  56   =>  lipRise >  72
   *     legibility   lipRise - 16 <= 135  =>  lipRise <= 151
   *
   * 104 gives a rise of 88: **32 clear of the denial bar and 47 clear of the
   * legibility bar.** The player stands on the rim, looks across the mouth,
   * sees the lip and the tread on top of it, and cannot get there. The only
   * remaining move is down.
   */
  lipRise: 104,
  /**
   * The lip overhangs the mouth, and this is the one piece of the landmark a
   * heightfield cannot express.
   *
   * Three things it buys, in the order they matter. It **roofs the cave mouth**,
   * so the shaft reads as an opening *into* something rather than as a hole
   * that happens to be next to a mountain. It makes the lip read as a lip
   * rather than as the top of a wall. And it puts solid rock where
   * `surfaceCarve` left sky, which is the only reason this module needs a
   * `kind` pass at all — without it the two-pass idiom would be ceremony.
   *
   * It sits 88 cells above the top of the shaft's rim, so it is nowhere near
   * the ability-free ascent, which leaves the shaft over the *west* lining at
   * `SURFACE_Y - RIM_PROUD` (`tests/traversal.test.ts`).
   */
  lipJut: 12,
  lipThick: 10,
  /**
   * The flank, as a staircase — **gate 2**, and the bound is the header's.
   *
   *     denial       flankStep >  FREE_APEX 56
   *     legibility   flankStep <= SCREEN_H / 2 = 135
   *
   * 88 again, and deliberately the same number as the lip's rise: the mountain
   * refuses in one unit, six times, and a player who has been refused once
   * knows what the rest of it is going to say.
   *
   * The tread lengths are the other half and they carry two separate jobs.
   *
   * The **bound** is `docs/27` §6.2-shaped: the branch had to leave `room >= 22`
   * so that the last rung under it was somewhere a body could *stand* rather
   * than somewhere it could not be, and a tread has to be somewhere a body can
   * stand and take a run-up. The shortest is 76 — seven body widths.
   *
   * The **shape** is a finding from the offline sweep and it is worth writing
   * down, because the first build got it wrong in a way no assertion could see.
   * With every tread the same length the mountain is geometrically correct,
   * denies exactly as it should, passes every gate test — and reads as a **bar
   * chart**. `docs/27` §8's thesis is that an authored shape is what makes a
   * place, and a staircase of identical blocks is not a shape, it is a plot.
   * Treads that shorten toward the summit give the same six risers a concave
   * profile that reads as a mountain at a glance. **Nothing about the gates
   * moved**: the gate is the riser, the silhouette is the treads, and the two
   * are separable exactly the way the tree's bough is separable from the leaf
   * clumps that make it look enormous.
   */
  flankStep: 88,
  treadLens: [180, 132, 104, 88, 76] as readonly number[],
  crestLen: 84,
  /**
   * The desert beyond — **gate 3**, and the promise the whole funnel is making.
   *
   * Drawn and unreachable. `docs/27` §7 item 6 is the bound: what a gate
   * withholds must be visible from where it is withheld, and the place this is
   * withheld from is the crest — the first thing any ability that clears the
   * flank will put the player on. So the desert has to be inside one camera of
   * the crest's east edge:
   *
   *     desertDrop <= SCREEN_H / 2 = 135      and    gap <= SCREEN_W / 2 = 240
   *
   * 120, and the gap is zero — the mesa begins at the crest's east edge. Step
   * onto the crest and the desert is on screen the same frame.
   *
   * Its east face is a sheer 424-cell drop back to the seeded surface, which is
   * `CLIFF_X` mirrored and reads as a mesa edge: nobody walks in from the east
   * either.
   */
  desertDrop: 120,
  desertLen: 400,
  /** Thickness of the crust on every exposed face. */
  rind: 5,
} as const;

/** How far `descentShaft` stands its rim proud of the ground. `SURFACE_Y - 16`. */
export const RIM_PROUD = 16;

/** Top of the cave's east lip, and the tread the mountain starts from. */
export function lipTop(): number {
  return MOUNTAIN.ground - MOUNTAIN.lipRise;
}

/** Risers above the lip. Derived from the tread table, so a tread cannot go missing. */
export function treads(): number {
  return MOUNTAIN.treadLens.length;
}

/** The crest: the top of the flank staircase. */
export function crestY(): number {
  return lipTop() - treads() * MOUNTAIN.flankStep;
}

/**
 * The desert's floor.
 *
 * **Derived, not authored**, and for `docs/27` §7 item 7's reason: the crest is
 * where the promise is read from, so the desert's elevation has to track the
 * crest's. Written down as a literal, deepening the mountain by one riser would
 * move the crest 88 cells and leave the desert where it was — the drop would
 * quietly grow past one camera and the promise would stop being visible from
 * the only place it is made. A mutation to `treadLens` or to `flankStep` is
 * caught because this reads both.
 */
export function desertY(): number {
  return crestY() + MOUNTAIN.desertDrop;
}

interface Level {
  x0: number;
  x1: number;
  /** Ground line of this level. */
  g: number;
  /** Ground line either side, for the crust test. `MOUNTAIN.ground` outside. */
  gWest: number;
  gEast: number;
  desert: boolean;
}

/**
 * The profile, as a table.
 *
 * Derived from the constants at module load rather than authored a second time,
 * the way `greatTree.ts` derives `FOLIAGE` from `BOUGHS`: a riser that moves
 * takes every level above it along.
 */
const LEVELS: readonly Level[] = (() => {
  const out: Level[] = [];
  let x = MOUNTAIN.caveLipX;
  for (let k = 0; k <= treads(); k++) {
    const len = k === treads() ? MOUNTAIN.crestLen : MOUNTAIN.treadLens[k];
    out.push({
      x0: x,
      x1: x + len - 1,
      g: lipTop() - k * MOUNTAIN.flankStep,
      gWest: 0,
      gEast: 0,
      desert: false,
    });
    x += len;
  }
  out.push({
    x0: x,
    x1: x + MOUNTAIN.desertLen - 1,
    g: desertY(),
    gWest: 0,
    gEast: 0,
    desert: true,
  });
  for (let i = 0; i < out.length; i++) {
    out[i].gWest = i === 0 ? MOUNTAIN.ground : out[i - 1].g;
    out[i].gEast = i === out.length - 1 ? MOUNTAIN.ground : out[i + 1].g;
  }
  return out;
})();

/** Every level of the profile, west to east. The crest is the last non-desert one. */
export function levels(): readonly { x0: number; x1: number; g: number; desert: boolean }[] {
  return LEVELS;
}

/** The crest's span, which is where the desert is read from. */
export function crest(): { x0: number; x1: number; y: number } {
  const c = LEVELS[treads()];
  return { x0: c.x0, x1: c.x1, y: c.g };
}

/** The desert's span. Drawn, and no body reaches it. */
export function desert(): { x0: number; x1: number; y: number } {
  const d = LEVELS[LEVELS.length - 1];
  return { x0: d.x0, x1: d.x1, y: d.g };
}

/**
 * Where the desert pays — docs/27 §12 item 6.
 *
 * The pool is the west half's reward for exploring the "wrong" direction;
 * this is the east half's, one ability later: a cache on the mesa floor, a
 * body's reach past the crest's east edge, for whoever the climb finally
 * admits. Twenty-four cells in, so it is inside the same camera that shows
 * the desert from the crest (§7 item 6's bound: the drop is 120 and the gap
 * is zero, so anything in the first 240 columns is on screen the frame the
 * promise is) — the reward is *visible from the refusal*, which is the whole
 * soft-gate thesis paying out at its largest scale.
 *
 * **Derived, not authored**, `desertY()`'s argument verbatim: the crest is
 * where this is seen from and the mesa is what it stands on, so both
 * coordinates track the tread table. A literal here would strand the reward
 * in the air the day the mountain grows a riser.
 */
export function desertCacheSite(): { x: number; y: number } {
  const d = desert();
  return { x: d.x0 + 24, y: d.y - 1 };
}

/**
 * The authored ground line at a column, or `null` where the seed still owns it.
 *
 * `WorldGen.surfaceHeight` returns this outright inside the span — see the
 * header. No damping, no easing, no seed: the mountain does not stand on the
 * heightfield, it **is** the heightfield.
 */
export function mountainGround(worldX: number): number | null {
  if (worldX < MOUNTAIN.caveLipX) return null;
  for (const l of LEVELS) {
    if (worldX >= l.x0 && worldX <= l.x1) return l.g;
  }
  return null;
}

/** The whole authored volume, as a clipping rect. Chunks outside it skip. */
export const MOUNTAIN_BOUNDS = {
  x0: MOUNTAIN.caveLipX - MOUNTAIN.lipJut,
  x1: LEVELS[LEVELS.length - 1].x1,
  y0: crestY(),
  /**
   * One cell *above* the ground line, and it is `greatTree.ts`'s reason
   * verbatim: `docs/27` §7 item 3, a landmark stops at the ground line and the
   * ground owns the ground. Everything at `MOUNTAIN.ground` and below is
   * `surfaceCarve`'s and the seed's, which is also why this landmark costs a
   * quarter of a million cells rather than a million.
   */
  y1: MOUNTAIN.ground - 1,
};

/**
 * What the mountain puts at a world cell.
 *
 * A material id, `VOID` where it carves, or `MOUNTAIN_NONE` where it is absent.
 * This is the whole shape; both generator passes read it and nothing else.
 *
 * It carves nowhere today — the cave is `descentShaft`, which was already the
 * intended path and the only reachable one — but the `VOID` case is kept
 * because `mountainKindOf` has to be able to express it and a shape that cannot
 * say "air" is a shape that will be given a second, disagreeing mechanism the
 * first time it needs one.
 */
export function mountainAt(wx: number, wy: number): number {
  if (wx < MOUNTAIN_BOUNDS.x0 || wx > MOUNTAIN_BOUNDS.x1) return MOUNTAIN_NONE;
  if (wy < MOUNTAIN_BOUNDS.y0 || wy > MOUNTAIN_BOUNDS.y1) return MOUNTAIN_NONE;

  // ---- 1. the lip's overhang, which is the only mass off the heightfield.
  const top = lipTop();
  if (wx < MOUNTAIN.caveLipX) {
    if (wy < top || wy >= top + MOUNTAIN.lipThick) return MOUNTAIN_NONE;
    const d = Math.min(
      wy - top,
      MOUNTAIN.caveLipX - 1 - wx,
      top + MOUNTAIN.lipThick - 1 - wy,
    );
    return d < MOUNTAIN.rind ? MAT.LIMESTONE : MAT.GRANITE;
  }

  // ---- 2. the profile: solid from the level's ground line down to the world's.
  for (const l of LEVELS) {
    if (wx < l.x0 || wx > l.x1) continue;
    if (wy < l.g) return MOUNTAIN_NONE;
    let depth = wy - l.g;
    // A face is exposed only where the neighbouring level's ground is *below*
    // this cell; buried sides get no crust, which is what stops a rind
    // appearing down the middle of the mountain at every riser.
    if (wy < l.gWest && wx - l.x0 < depth) depth = wx - l.x0;
    if (wy < l.gEast && l.x1 - wx < depth) depth = l.x1 - wx;
    if (l.desert) return depth < MOUNTAIN.rind ? MAT.SAND : MAT.SANDSTONE;
    return depth < MOUNTAIN.rind ? MAT.LIMESTONE : MAT.GRANITE;
  }
  return MOUNTAIN_NONE;
}

/** Does this chunk's world rect meet the mountain at all? The chunk-level early out. */
export function chunkTouchesMountain(x0: number, y0: number, x1: number, y1: number): boolean {
  return !(x1 < MOUNTAIN_BOUNDS.x0 || x0 > MOUNTAIN_BOUNDS.x1
    || y1 < MOUNTAIN_BOUNDS.y0 || y0 > MOUNTAIN_BOUNDS.y1);
}

/**
 * The `Kind` a mountain material resolves to.
 *
 * `greatTree.ts` `treeKindOf`'s twin, and exported for the same reason: `Kind`
 * is `worldgen.ts`-private, so the three codes are handed in and this function
 * cannot silently disagree with the enum.
 *
 * **Nothing on this landmark is one-way today**, so the middle branch is
 * unobservable and a mutation collapsing it to `kSolid` is green — measured,
 * and recorded rather than papered over. `greatTree.test.ts` §4 found the same
 * branch had exactly one consumer (`deposits`, which skips `K_PLATFORM`), and
 * the mountain has no scenery for that consumer to act on. The branch stays
 * because the alternative is that the first one-way cell placed here is placed
 * against a function that cannot express it.
 */
export function mountainKindOf(
  mat: number, kOpen: number, kPlatform: number, kSolid: number,
): number {
  if (mat === VOID) return kOpen;
  return M.oneWay[mat] !== 0 ? kPlatform : kSolid;
}
